import {
  ChatBubbleOutline,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, Chip, InputAdornment, TextField } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDemoForm } from '../../../context/DemoFormContext';

const MIN_WIDTH_EMPTY = 170;
const MIN_WIDTH_FILLED = 240;

const LanguageFilterAutocomplete: React.FC = () => {
  const { apiLanguages, languages, setFormLanguages } = useDemoForm();
  const [open, setOpen] = useState(false);

  const options = useMemo(() => Object.keys(apiLanguages || {}), [apiLanguages]);
  const value = languages || [];
  const isEmpty = value.length === 0;

  const handleChange = useCallback(
    (_e: React.SyntheticEvent, newValue: string[]) => setFormLanguages(newValue),
    [setFormLanguages],
  );

  return (
    <Autocomplete<string, true, true, false>
      multiple
      size="small"
      disableCloseOnSelect
      disableClearable
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      onChange={handleChange}
      renderOption={(optionProps, option, { selected }) => {
        const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
          key: React.Key;
        };
        return (
          <li key={key} {...rest}>
            <Checkbox
              size="small"
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              checked={selected}
              sx={{ mr: 0.5, p: 0.5 }}
            />
            {option}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option}
              color="primary"
              variant="outlined"
              size="small"
              sx={{
                height: '20px',
                fontSize: '0.7rem',
                '& .MuiChip-label': { px: 0.75 },
                '& .MuiChip-deleteIcon': { fontSize: '14px', mr: 0.25 },
              }}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          // Floating-label style so it visually matches the "Primary Sort"
          // FormControl. The label collapses up to the border when there are
          // chips or focus, otherwise sits inline as a placeholder.
          label="Language Filter"
          // Suppress password-manager autofill prompts on this autocomplete
          // input so 1Password / LastPass / Bitwarden don't overlay their
          // icon on the chips.
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
            'data-1p-ignore': 'true',
            'data-lpignore': 'true',
            'data-form-type': 'other',
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>
                    <ChatBubbleOutline fontSize="small" />
                  </Box>
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        // Stay compact when nothing is selected; grow once chips appear so
        // they're not cramped. Capped at a sensible max so the row doesn't
        // sprawl with many languages selected.
        minWidth: isEmpty ? MIN_WIDTH_EMPTY : MIN_WIDTH_FILLED,
        maxWidth: 360,
        flex: isEmpty ? '0 0 auto' : '1 1 auto',
        '& .MuiOutlinedInput-root': {
          minHeight: 40,
        },
      }}
    />
  );
};

export default LanguageFilterAutocomplete;
